import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Alert,
} from '@mui/material';
import { AxiosError } from 'axios';
import { useFormik } from 'formik';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { usePrevious, useProviderTypes } from 'hooks';
import { TranslationNamespace } from 'i18n';
import {
  CreateProviderTypeDto,
  ProviderType,
  UpdateProviderTypeDto,
} from 'types';
import { validationUtils } from 'utils';

type Props = {
  open: boolean;
  data?: ProviderType;
  onClose: () => void;
};

type FormValues = {
  code: string;
  name: string;
  description: string;
};

const KEYS = {
  create_title: 'pages.provider_types.dialog.create_title',
  edit_title: 'pages.provider_types.dialog.edit_title',
  fields: {
    code: 'pages.provider_types.dialog.fields.code',
    name: 'pages.provider_types.dialog.fields.name',
    description: 'pages.provider_types.dialog.fields.description',
  },
  messages: {
    system_type_info: 'pages.provider_types.messages.system_type_info',
  },
} as const;

export const ProviderTypeDialog: React.FC<Props> = ({
  open,
  data,
  onClose,
}) => {
  const { t: tAdmin } = useTranslation(TranslationNamespace.Admin);
  const t = tAdmin as unknown as (key: string) => string;
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);

  const prevOpen = usePrevious(open);
  const isNew = !data?.id;

  const { createMutation, updateMutation } = useProviderTypes();

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        code: Yup.string()
          .required(tCommon('errors.required') as string)
          .max(
            100,
            tCommon('errors.max', {
              defaultValue: 'Max 100 characters',
            }) as string,
          )
          .matches(
            /^[a-z0-9_-]+$/,
            tCommon('errors.invalid', {
              defaultValue: 'Invalid format',
            }) as string,
          ),
        name: Yup.string()
          .required(tCommon('errors.required') as string)
          .max(
            255,
            tCommon('errors.max', {
              defaultValue: 'Max 255 characters',
            }) as string,
          ),
        description: Yup.string(),
      }),
    [tCommon],
  );

  const formik = useFormik<FormValues>({
    initialValues: {
      code: '',
      name: '',
      description: '',
    },
    validationSchema,
    validateOnMount: true,
    onSubmit: async (values, helpers) => {
      try {
        if (isNew) {
          const payload: CreateProviderTypeDto = {
            code: values.code,
            name: values.name,
            description: values.description || undefined,
          };
          await createMutation.mutateAsync(payload);
        } else {
          const payload: UpdateProviderTypeDto = {
            name: values.name,
            description: values.description || undefined,
          };
          await updateMutation.mutateAsync({ id: data!.id, data: payload });
        }
        onClose();
      } catch (error) {
        if (error instanceof AxiosError) {
          helpers.setErrors(validationUtils.getFormErrors(error));
        }
      }
    },
  });

  useEffect(() => {
    if (open && !prevOpen) {
      if (data) {
        formik.setValues({
          code: data.code,
          name: data.name,
          description: data.description || '',
        });
      } else {
        formik.resetForm();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prevOpen, data]);

  const handleClose = useCallback(() => {
    formik.resetForm();
    onClose();
  }, [formik, onClose]);

  const title = isNew ? t(KEYS.create_title) : t(KEYS.edit_title);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <form onSubmit={formik.handleSubmit}>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {data?.isSystem && (
              <Alert severity="info">{t(KEYS.messages.system_type_info)}</Alert>
            )}

            <TextField
              fullWidth
              label={t(KEYS.fields.code)}
              name="code"
              value={formik.values.code}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.code && !!formik.errors.code}
              helperText={formik.touched.code && formik.errors.code}
              disabled={!isNew}
              placeholder="my_provider"
            />

            <TextField
              fullWidth
              label={t(KEYS.fields.name)}
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.name && !!formik.errors.name}
              helperText={formik.touched.name && formik.errors.name}
              placeholder="My Provider"
            />

            <TextField
              fullWidth
              multiline
              rows={3}
              label={t(KEYS.fields.description)}
              name="description"
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.description && !!formik.errors.description}
              helperText={
                formik.touched.description && formik.errors.description
              }
            />
          </Stack>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{tCommon('buttons.cancel')}</Button>
        <Button
          variant="contained"
          onClick={() => formik.handleSubmit()}
          disabled={!formik.isValid || formik.isSubmitting}
        >
          {tCommon('buttons.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
